import { AxiosResponse } from 'axios';
import useSWR from 'swr';

import { useMemo, useState } from 'react';
import { apiInstance } from 'lib/utils/axios';
import { API_ROUTES } from 'lib/api-routes';
import {
  SponsorCompanyAttributes,
  sponsorCompaniesResponse,
} from 'lib/models/sponsor-companies';
import { parseArrayResponse } from 'lib/utils/parser';
import { useNotification } from './useNotification';

interface PartnershipsReturnType {
  isLoading: boolean;
  isPartnersDataLoading: boolean;
  partnerSocietes: SponsorCompanyAttributes[];
  inConversationPartners: SponsorCompanyAttributes[];
  removePartner: (id: string) => Promise<void | string>;
}

export const usePartnerships = (): PartnershipsReturnType => {
  const notificationInstance = useNotification();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: partnerSocietiesResponse, isLoading: partnerSocietiesLoading } =
    useSWR<AxiosResponse<sponsorCompaniesResponse>>(
      API_ROUTES.COMPANY_SPONSORSHIPS,
      apiInstance.get,
      {
        revalidateOnFocus: false,
      }
    );

  const partnerSocietes = useMemo(() => {
    if (!partnerSocietiesResponse) return [];
    return parseArrayResponse<SponsorCompanyAttributes>(
      partnerSocietiesResponse.data
    );
  }, [partnerSocietiesResponse]);

  const {
    data: inConversationPartnersResponse,
    isLoading: inConversationPartnersLoading,
    mutate: mutateInConversationPartners,
  } = useSWR<AxiosResponse<sponsorCompaniesResponse>>(
    `${API_ROUTES.COMPANY_SPONSORSHIP_INVITE_LIST}?in_conversation=true`,
    apiInstance.get,
    {
      revalidateOnFocus: false,
    }
  );

  const inConversationPartners = useMemo(() => {
    if (!inConversationPartnersResponse) return [];
    return parseArrayResponse<SponsorCompanyAttributes>(
      inConversationPartnersResponse.data
    );
  }, [inConversationPartnersResponse]);

  const isPartnersDataLoading = useMemo(
    () => partnerSocietiesLoading || inConversationPartnersLoading,
    [partnerSocietiesLoading, inConversationPartnersLoading]
  );

  const removePartner = async (id: string) => {
    try {
      setIsLoading(true);
      const path = `${API_ROUTES.COMPANY_SPONSORSHIP_INVITE_LIST}/${id}`;
      const response = await apiInstance.delete(path);
      await mutateInConversationPartners();
      notificationInstance.success({
        title: 'Success',
        message: 'Partner removed successfully.',
      });
      return response.data.notice;
    } catch (error) {
      notificationInstance.error(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    isPartnersDataLoading,
    partnerSocietes,
    inConversationPartners,
    removePartner,
  };
};
